import { ProgressBar as FluentProgressBar, Text, makeStyles, tokens } from '@fluentui/react-components';
import type { ProgressUpdate } from '@/types/progress';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  stepText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  completedText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  errorText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
});

interface ProgressBarProps {
  progress: ProgressUpdate;
}

export function ProgressBar({ progress }: ProgressBarProps) {
  const styles = useStyles();

  const progressValue = progress.totalSteps > 0 ? (progress.currentStep / progress.totalSteps) : 0;

  const getProgressColor = () => {
    if (progress.isComplete) {
      return progress.success ? 'success' : 'error';
    }
    return 'brand';
  };

  const renderProgressText = () => {
    if (progress.isComplete) {
      const textStyle = progress.success ? styles.completedText : styles.errorText;
      return (
        <Text className={textStyle}>
          {progress.message || progress.description}
        </Text>
      );
    }

    return (
      <>
        <Text className={styles.description}>{progress.description}</Text>
        <Text className={styles.stepText}>
          Step {progress.currentStep} of {progress.totalSteps}
        </Text>
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.progressContainer}>
        {renderProgressText()}
        <FluentProgressBar
          value={progressValue}
          color={getProgressColor()}
          thickness="medium"
        />
      </div>
    </div>
  );
}